import { useEffect } from 'react';
import { useSharedElement } from '../contexts/SharedElementContext';

interface UseSharedElementIdOptions {
  /**
   * The ID to compare with the shared element ID
   */
  id: string | undefined;
  
  /**
   * The maximum time (in ms) to wait for animation completion before forcibly clearing the shared element ID
   * @default 1000
   */
  maxWaitTime?: number;
}

/**
 * Hook for managing shared element ID state with automatic cleanup
 * 
 * @param options Options for shared element ID management
 * @returns The current shared element ID
 */
export function useSharedElementId({ id, maxWaitTime = 1000 }: UseSharedElementIdOptions) {
  const { sharedElementId, setSharedElementId } = useSharedElement();

  // Handle shared element ID lifecycle
  useEffect(() => {
    // Clear shared element ID after a maximum time to ensure it doesn't get stuck
    let timeoutId: number | undefined;
    
    if (id && sharedElementId !== id) {
      // If we have an ID but it doesn't match, clear the shared element ID
      setSharedElementId(null);
    } else if (sharedElementId === id) {
      // If we have a match, set a timeout to clear it automatically
      timeoutId = window.setTimeout(() => {
        setSharedElementId(null);
      }, maxWaitTime);
    }
    
    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      setSharedElementId(null);
    };
  }, [id, sharedElementId, setSharedElementId, maxWaitTime]);

  return sharedElementId;
} 